import { CanAccess, RecordContextProvider } from 'react-admin';
import { useGetOne, useTranslate } from 'ra-core';
import { Skeleton } from '@/components/ui/skeleton';
import ImageWithOverlayBorder from '../../../components/ui/ImageWithOverlayBorder';
import { Offer } from '../types';
import LabeledHorizontal from '../../../components/raExtend/labels/LabeledHorizontal';
import ListField from '../../../components/raExtend/fields/ListField';
import OneLineTextField from '../../../components/raExtend/fields/OneLineTextField';
import SuccessButton from '../../../components/ui/SuccesButton';
import { CheckCircle, Ban, RefreshCw } from 'lucide-react';
import ErrorButton from '../../../components/ui/ErrorButton';
import SecondaryButton from '../../../components/ui/SecondaryButton';
import { useState } from 'react';
import AcceptOfferModal from '../negotiationModals/AcceptOfferModal';
import DeclineOfferModal from '../negotiationModals/DeclineOfferModal';
import CounterOfferModal from '../negotiationModals/CounterOfferModal';
import ContentWrapper from './ContentWrapper';
import NegotiationHistory from '../negotiation/NegotiationHistory';
import Pricing from './Pricing';
import { DEFAULT_DATE_FIELD_OPTIONS } from '../../../constants';
import BaseDrawer from './BaseDrawer';
import PurchaseOrderCard from './PurchaseOrderCard';
import OfferDrawerLastNegotiation from './OfferDrawerLastNegotiation';
import OfferCreativeList from './creatives/OfferCreativeList';
import TrackingLinks from './creatives/TrackingLinks';
import PreferredCopies from './PreferredCopies';
import LegacyCreatives from './creatives/LegacyCreatives';

type OfferDrawerContentProps = {
  id: string;
  onCreativeOpen: (creativeId: string) => void;
  isAccepted?: boolean;
  setIsAccepted?: (isAccepted: boolean) => void;
};

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
      {children}
    </p>
  );
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en', DEFAULT_DATE_FIELD_OPTIONS).format(
    new Date(value)
  );
}

function OfferDrawerContent({
  id,
  onCreativeOpen,
  isAccepted,
  setIsAccepted,
}: OfferDrawerContentProps) {
  const {
    data: offer,
    isPending,
    refetch: refetchOffer,
  } = useGetOne<Offer>('offers', {
    id,
  });
  const [isAcceptOpen, setAcceptOpen] = useState(false);
  const [isDeclineOpen, setDeclineOpen] = useState(false);
  const [isCounterOpen, setCounterOpen] = useState(false);
  const [isNegotiationHistoryOpen, setIsNegotiationHistoryOpen] =
    useState(false);

  const t = useTranslate();

  const handleOpenCounterFromDecline = () => {
    setDeclineOpen(false);
    setCounterOpen(true);
  };

  if (isPending)
    return (
      <div className="space-y-6 animate-in fade-in-0 duration-300">
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 h-20 rounded-xl shrink-0" />
          <div className="space-y-2.5 flex-1">
            <Skeleton className="h-6 w-3/5" />
            <Skeleton className="h-4 w-2/5" />
          </div>
        </div>
        <Skeleton className="h-px w-full" />
        <div className="space-y-2.5">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
        <div className="space-y-2.5">
          <Skeleton className="h-3 w-12" />
          <Skeleton className="h-11 rounded-lg" />
          <Skeleton className="h-11 rounded-lg" />
        </div>
        <div className="space-y-2.5">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-11 rounded-lg" />
          <Skeleton className="h-11 rounded-lg" />
        </div>
      </div>
    );
  if (!offer || offer.status === 'voided') return null;

  return (
    <RecordContextProvider value={offer}>
      <div className="flex items-center gap-4">
        <ImageWithOverlayBorder
          src={offer.brand.logo}
          alt="Offer brand logo"
          width={80}
          height={80}
        />
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">
            {offer.brand.name}
          </h2>
          <p className="text-sm text-muted-foreground">{offer.name}</p>
        </div>
      </div>
      <hr className="border-border my-6" />
      <div className="flex flex-col gap-10">
        {/* pricing */}
        <div>
          <SectionLabel>{t('common.text.pricing')}</SectionLabel>
          <Pricing />
        </div>
        {/* negotiations */}
        {!(offer.status === 'accepted' || offer.status === 'active') && (
          <OfferDrawerLastNegotiation
            offer={offer}
            onOpenHistory={() => setIsNegotiationHistoryOpen(true)}
          />
        )}
        {/* dates */}
        <div>
          <SectionLabel>{t('common.text.dates')}</SectionLabel>
          <ContentWrapper>
            <LabeledHorizontal label={t('common.text.start')}>
              <span className="text-sm text-muted-foreground">
                {formatDate(offer.start_date)}
              </span>
            </LabeledHorizontal>
            <LabeledHorizontal label={t('common.text.end')}>
              <span className="text-sm text-muted-foreground">
                {formatDate(offer.end_date)}
              </span>
            </LabeledHorizontal>
          </ContentWrapper>
        </div>
        {/* targeting */}
        {(offer.geo || offer.interests) && (
          <div>
            <SectionLabel>{t('common.text.targeting')}</SectionLabel>
            <ContentWrapper>
              {offer.geo && (
                <LabeledHorizontal label={t('common.text.geo')}>
                  <ListField<Offer> source="geo" />
                </LabeledHorizontal>
              )}
              {offer.interests && (
                <LabeledHorizontal label={t('common.text.interests')}>
                  <ListField<Offer> source="interests" />
                </LabeledHorizontal>
              )}
            </ContentWrapper>
          </div>
        )}
        {/* goals */}
        {offer.goals && (
          <div>
            <SectionLabel>{t('common.text.goals')}</SectionLabel>
            <ContentWrapper>
              <ListField<Offer> source="goals" />
            </ContentWrapper>
          </div>
        )}
        {(offer.status === 'accepted' || offer.status === 'active') && (
          <>
            {Boolean(offer.creatives) && (
              <div>
                <SectionLabel>{t('common.text.creatives')}</SectionLabel>
                <LegacyCreatives creatives={offer.creatives} />
              </div>
            )}
            {/* Preferred copies */}
            {Boolean(offer.preferred_copies) && (
              <div>
                <SectionLabel>{t('common.text.preferred_copy')}</SectionLabel>
                <PreferredCopies preferredCopies={offer.preferred_copies} />
              </div>
            )}
            {/* Creatives */}
            <OfferCreativeList
              offerId={offer.id}
              onCreativeOpen={onCreativeOpen}
            />
            {/* Promo code */}
            {Boolean(offer.promo_code) && (
              <OneLineTextField<Offer>
                label={t('common.text.promo_code')}
                source="promo_code"
              />
            )}
            {/* Tracking links */}
            {!offer.offer_creatives?.length && offer.trackinglinks && (
              <div>
                <SectionLabel>Tracking links</SectionLabel>
                <TrackingLinks trackingLinks={offer.trackinglinks} />
              </div>
            )}
            <OfferDrawerLastNegotiation
              offer={offer}
              onOpenHistory={() => setIsNegotiationHistoryOpen(true)}
            />
          </>
        )}
        {/* Purchase order */}
        {Boolean(offer.po_url) && (
          <div>
            <SectionLabel>{t('common.text.purchase_order')}</SectionLabel>
            <PurchaseOrderCard
              url={offer.po_url}
              fileName={`purchase-order.pdf`}
            />
          </div>
        )}
      </div>

      <CanAccess action="edit" resource="offer-negotiations">
        {(offer.status === 'reviewing' ||
          offer.status === 'counter-in-review' ||
          offer.status === 'new-counter') && (
          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            <SuccessButton
              onClick={() => setAcceptOpen(true)}
              disabled={offer.status === 'counter-in-review'}
            >
              <CheckCircle className="size-4" />
              {t('offer.drawer.button.accept')}
            </SuccessButton>
            <ErrorButton
              onClick={() => setDeclineOpen(true)}
              disabled={offer.status === 'counter-in-review'}
            >
              <Ban className="size-4" />
              {t('offer.drawer.button.decline')}
            </ErrorButton>
            <SecondaryButton
              onClick={() => setCounterOpen(true)}
              disabled={offer.status === 'counter-in-review'}
            >
              <RefreshCw className="size-4" />
              {t('offer.drawer.button.counter')}
            </SecondaryButton>
          </div>
        )}

        {isAcceptOpen && (
          <AcceptOfferModal
            offer={offer}
            open={isAcceptOpen}
            onClose={() => setAcceptOpen(false)}
            refetchOffer={refetchOffer}
            isAccepted={isAccepted}
            setIsAccepted={setIsAccepted}
          />
        )}

        {isDeclineOpen && (
          <DeclineOfferModal
            offer={offer}
            open={isDeclineOpen}
            onClose={() => setDeclineOpen(false)}
            onCounter={handleOpenCounterFromDecline}
          />
        )}

        {isCounterOpen && (
          <CounterOfferModal
            offer={offer}
            open={isCounterOpen}
            onClose={() => setCounterOpen(false)}
          />
        )}
      </CanAccess>

      <BaseDrawer
        open={isNegotiationHistoryOpen}
        onClose={() => setIsNegotiationHistoryOpen(false)}
        className="max-w-xl"
      >
        <NegotiationHistory
          onClose={() => setIsNegotiationHistoryOpen(false)}
        />
      </BaseDrawer>
    </RecordContextProvider>
  );
}

export default OfferDrawerContent;
